export type ResponseCallback = ({
  completeResponse,
  contentType,
  responseChunk,
  done,
}: {
  completeResponse: any;
  contentType: 'text/plain' | 'application/json';
  responseChunk?: string;
  done: boolean;
}) => void;

// Call a single prompt and get an asynchronous response. The API is designed
// to support streaming, but the backend doesn't currently stream responses.
const executePrompt = async ({
  promptName,
  userPrompt,
  promptParams,
  responseCallback,
}: {
  promptName: string;
  userPrompt?: string;
  promptParams?: Record<string, string>;
  responseCallback?: ResponseCallback;
}) => {
  const response = await fetch('/assistant/chats/execute_prompt', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': window.csrfToken(),
    },
    body: JSON.stringify({
      prompt: promptName,
      user_prompt: userPrompt,
      prompt_params: promptParams,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Error executing prompt ${promptName}: ${response.statusText}`
    );
  }

  const result: any = await response.json();

  // Streaming
  responseCallback?.({
    done: true,
    completeResponse: result,
    contentType: 'application/json',
  });

  // Non-streaming
  return result;
};

type ExecutePromptParams = Parameters<typeof executePrompt>[0];

// Both streaming and non-streaming versions use the same underlying function until streaming is implemented.
const executePromptStreaming: (params: ExecutePromptParams) => Promise<void> =
  executePrompt;

const executePromptSync: (
  params: Omit<ExecutePromptParams, 'responseCallback'>
) => Promise<any> = executePrompt;

export { executePromptStreaming, executePromptSync as executePrompt };
